import { useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowRightLeft, Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type RowAction,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { Redirect } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useStore } from '@/shared/store/store-context';

import { DeleteRedirectDialog } from '../components/delete-redirect-dialog';
import { useRedirectColumns } from '../components/redirect-columns';
import { RedirectDialog } from '../components/redirect-dialog';
import { redirectFilters } from '../components/redirect-filters';
import { useRedirects } from '../hooks/use-redirects';

const ROUTE_ID = '/_authed/_active/s/$storeId/content/redirects' as const;
const ROUTE_PATH = '/s/$storeId/content/redirects' as const;

type Editing = { open: false } | { open: true; redirect: Redirect | null };

function Redirects() {
  const { t } = useTranslation('redirects');
  const store = useStore();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const { can } = usePermissions();
  const canWrite = can('content.write');
  const [editing, setEditing] = useState<Editing>({ open: false });
  const [deleting, setDeleting] = useState<Redirect | null>(null);

  const filters = useMemo(() => redirectFilters(t), [t]);
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const query = useRedirects(store.id, list.params);

  // Both actions write, so readers get no row menu at all.
  const rowActions = useMemo<Array<RowAction<Redirect>>>(
    () => [
      {
        id: 'edit',
        label: t('actions.edit'),
        icon: <Pencil aria-hidden />,
        onAction: (row) => setEditing({ open: true, redirect: row }),
      },
      {
        id: 'delete',
        label: t('actions.delete'),
        icon: <Trash2 aria-hidden />,
        variant: 'destructive',
        separatorBefore: true,
        onAction: (row) => setDeleting(row),
      },
    ],
    [t],
  );

  const columns = useRedirectColumns();
  const rows = query.data?.items ?? [];
  const table = useDataTable({
    mode: 'server',
    data: rows,
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'redirects',
  });

  const narrowed = Boolean(list.q) || Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmptyList = query.isSuccess && rows.length === 0 && !narrowed;

  const addButton = (tour?: string) => (
    <Can permission="content.write">
      <Button onClick={() => setEditing({ open: true, redirect: null })} data-tour={tour}>
        <Plus aria-hidden />
        {t('create.action')}
      </Button>
    </Can>
  );

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name })}
        actions={addButton('content.redirects.add')}
      />
      <Alert className="mb-4" data-tour="content.redirects.intro">
        <ArrowRightLeft aria-hidden />
        <AlertDescription>{t('intro')}</AlertDescription>
      </Alert>
      {isEmptyList ? (
        <EmptyState
          icon={ArrowRightLeft}
          title={t('empty.title')}
          description={t('empty.description')}
          action={addButton()}
          tour="content.redirects.empty"
          tourLink
        />
      ) : (
        <DataTable
          pillFilter="statusCode"
          table={table}
          tour={{ table: 'content.redirects.table' }}
          search={{ placeholder: t('search'), label: t('search') }}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          rowActions={canWrite ? rowActions : undefined}
          getRowLabel={(row) => row.fromPath}
          resourceName={{ singular: t('resource.one'), plural: t('resource.other') }}
          emptyState={{ title: t('empty.filtered') }}
        />
      )}
      {editing.open ? (
        <RedirectDialog
          key={editing.redirect?.id ?? 'new'}
          storeId={store.id}
          redirect={editing.redirect}
          open
          onOpenChange={(open) => {
            if (!open) setEditing({ open: false });
          }}
        />
      ) : null}
      <DeleteRedirectDialog
        storeId={store.id}
        redirect={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </PageContent>
  );
}

export function RedirectsPage() {
  return (
    <FeatureErrorBoundary name="redirects.list">
      <Redirects />
    </FeatureErrorBoundary>
  );
}
